import { useState, useEffect } from 'react';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, Loader2, Mail, User } from 'lucide-react';

interface Parte {
  id: string;
  nome_completo: string;
  tipo_parte: string;
  email: string | null;
  deve_receber_contrato: boolean | null;
}

interface SelecionarPartesAprovacaoProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (partesSelecionadas: string[]) => void;
  processoId: string;
}

export const SelecionarPartesAprovacao = ({
  isOpen,
  onClose,
  onConfirm,
  processoId,
}: SelecionarPartesAprovacaoProps) => {
  const { toast } = useToast();
  const [partes, setPartes] = useState<Parte[]>([]);
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && processoId) {
      loadPartes();
    }
  }, [isOpen, processoId]);

  const loadPartes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('partes_processo')
        .select('id, nome_completo, tipo_parte, email, deve_receber_contrato')
        .eq('processo_id', processoId);

      if (error) throw error;

      setPartes(data || []);

      // Pré-selecionar partes que devem receber contrato
      const preSelected = new Set(
        (data || [])
          .filter(p => p.deve_receber_contrato && p.email)
          .map(p => p.id)
      );
      setSelecionadas(preSelected);
    } catch (error) {
      console.error('Erro ao carregar partes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as partes do processo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleParte = (parteId: string) => {
    const newSelecionadas = new Set(selecionadas);
    if (newSelecionadas.has(parteId)) {
      newSelecionadas.delete(parteId);
    } else {
      newSelecionadas.add(parteId);
    }
    setSelecionadas(newSelecionadas);
  };

  const handleConfirmar = async () => {
    if (selecionadas.size === 0) {
      toast({
        title: "Atenção",
        description: "Selecione pelo menos uma parte para receber o contrato",
        variant: "destructive",
      });
      return;
    }

    // Verificar se todas as partes selecionadas têm email
    const semEmail = partes.filter(p => 
      selecionadas.has(p.id) && !p.email
    );

    if (semEmail.length > 0) {
      toast({
        title: "Atenção",
        description: `As seguintes partes não têm email cadastrado: ${semEmail.map(p => p.nome_completo).join(', ')}`,
        variant: "destructive",
      });
      return;
    }

    try {
      // Atualizar campo deve_receber_contrato das partes
      const updates = partes.map(parte => ({
        id: parte.id,
        deve_receber_contrato: selecionadas.has(parte.id)
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('partes_processo')
          .update({ deve_receber_contrato: update.deve_receber_contrato })
          .eq('id', update.id);

        if (error) throw error;
      }

      onConfirm(Array.from(selecionadas));
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar partes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar as partes",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Selecionar Destinatários do Contrato</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Selecione quais partes receberão o link para revisão do contrato por email:
            </p>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {partes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma parte cadastrada neste processo
                </div>
              ) : (
                partes.map((parte) => {
                  const isSelected = selecionadas.has(parte.id);
                  const hasEmail = !!parte.email;

                  return (
                    <div
                      key={parte.id}
                      className={`
                        border rounded-lg p-4 cursor-pointer transition-all
                        ${isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
                        ${!hasEmail ? 'opacity-50 cursor-not-allowed' : ''}
                      `}
                      onClick={() => hasEmail && toggleParte(parte.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {isSelected ? (
                              <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-border flex-shrink-0" />
                            )}
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium">{parte.nome_completo}</span>
                            </div>
                          </div>

                          <div className="ml-7 space-y-1">
                            <div className="text-sm text-muted-foreground">
                              <span className="font-medium">Função:</span> {parte.tipo_parte}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {hasEmail ? (
                                <span>{parte.email}</span>
                              ) : (
                                <span className="text-red-500">Email não cadastrado</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>📧 {selecionadas.size} {selecionadas.size === 1 ? 'parte selecionada' : 'partes selecionadas'}</strong>
                {selecionadas.size > 0 && ' receberá(ão) o link de revisão por email'}
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmar}
                disabled={selecionadas.size === 0}
              >
                Enviar para {selecionadas.size} {selecionadas.size === 1 ? 'parte' : 'partes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
